import { Table as Tb } from 'antd';
import { Dispatch, SetStateAction } from 'react';
import styled from 'styled-components';
import {
  DeleteOutlined,
  StopOutlined,
  EyeOutlined,
  SolutionOutlined,
  FlagOutlined,
  RollbackOutlined,
  PhoneOutlined,
  PushpinOutlined,
  PushpinFilled
} from '@ant-design/icons';
import {
  ALL_NEW_TITLE,
  PENDING_EXCLUSION,
  PODIUM_TITLES,
  WATCHLIST,
  RETURN,
  ALL_EXCLUDED,
  ALL_EXCLUDED_RR,
  ALL_TITLES_RR,
  COMPETITORS,
  MANUALLY_ADDED,
  POPULAR,
  RISING_STARS,
  SEND_TO_CRM,
  SENT_TO_CRM_RR,
  TRENDING,
  WATCHLIST_RR,
  PENDING_EXCLUSION_RR,
  PODIUM_TITLES_RR,
  HOT_TITLES,
  CONTACTED_RR,
  CONTACTED,
  MUTED_AUTHORS,
  MUTED_AUTHORS_RR,
  WATCHLIST_AO3,
  CONTACTED_AO3,
  POPULAR_AO3,
  PODIUM_TITLES_AO3,
  PENDING_EXCLUSION_AO3,
  ALL_EXCLUDED_AO3,
  MUTED_AUTHORS_AO3,
  SENT_TO_CRM_AO3
} from '../../core/constants';
import { ReactComponent as UnmuteIcon } from '../../assets/icons/bell-off-fill-unmute.svg';
import { ReactComponent as MuteIcon } from '../../assets/icons/bell-off-outline-mute.svg';

const MenuItem = styled.div`
  color: ${({ hide }) => hide && '#d6d6d6 !important'};
  cursor: ${({ hide }) => hide && 'default'};
  span {
    color: ${({ hide }) => hide && '#d6d6d6 !important'};
    margin-right: 6px;
  }
  .icons-flag {
    color: orange;
  }
  .icons-eye {
    color: green;
  }
  .icons-pending-exclusion {
    color: rgb(153, 153, 42);
  }
  .icons-trash {
    color: red;
  }
  .icons-block {
    color: red;
  }
  .icons-pin {
    color: #6949EA;
  }
  .icons-unpin {
    color: #6949EA;
  }
  .icons-mute {
    color: red;
  }
  .icons-podium-title {
    color: #8108b3;
  }
  .icons-rollback {
    color: grey;
  }
  .icons-mute {
    padding: 2px 2px 0 0;
    margin-right: 2px;
    svg, path, rect, circle {
      fill: ${({ hide }) => hide ? '#d6d6d6' : '#FA8C16'} !important;
    }
  }
  .icons-unmute {
    padding: 2px 2px 0 0;
    margin-right: 2px;
    svg, path, rect, circle {
      fill: ${({ hide }) => hide ? '#d6d6d6' : '#FA8C16'} !important;
    }
  }
`;

type ModalHandlers = {
  setOpenDeleteModal: (val: boolean) => void;
  setOpenBlockAuthorsModal: (val: boolean) => void;
  setOpenMuteAuthorsModal?: (val: boolean) => void;
  setOpenUnmuteAuthorsModal?: (val: boolean) => void;
  setOpenPinUnpinModal?: (val: 'pin' | 'unpin' | '') => void;
  showMute?: boolean;
  showUnmute?: boolean;
};

export const commonOptions = (
  selectedTitles,
  modals: ModalHandlers
) => {
  const {
    setOpenDeleteModal,
    setOpenBlockAuthorsModal,
    setOpenMuteAuthorsModal,
    setOpenUnmuteAuthorsModal,
    showMute,
    showUnmute
  } = modals;

  return [
    {
      key: 'blockAll',
      text: (
        <MenuItem hide={!selectedTitles?.length}>
          <StopOutlined className='icons-block' />
          Block Selected Author(s)
        </MenuItem>
      ),
      onSelect: () =>
        selectedTitles?.length && setOpenBlockAuthorsModal(true)
    },

    ...(showMute
      ? [{
        key: 'muteAll',
        text: (
          <MenuItem hide={!selectedTitles?.length}>
            <MuteIcon className='icons-mute' />
              Mute all
          </MenuItem>
        ),
        onSelect: () =>
          selectedTitles?.length && setOpenMuteAuthorsModal?.(true)
      }]
      : []),

    ...(showUnmute
      ? [{
        key: 'unmuteAll',
        text: (
          <MenuItem hide={!selectedTitles?.length}>
            <UnmuteIcon className='icons-unmute' />
              Unmute all
          </MenuItem>
        ),
        onSelect: () =>
          selectedTitles?.length && setOpenUnmuteAuthorsModal?.(true)
      }]
      : []),

    {
      key: 'deleteAll',
      text: (
        <MenuItem hide={!selectedTitles?.length}>
          <DeleteOutlined className='icons-trash' />
          Delete all
        </MenuItem>
      ),
      onSelect: () =>
        selectedTitles?.length && setOpenDeleteModal(true)
    },

    Tb.SELECTION_INVERT
  ];
};

export const toPendingExclusion = (selectedTitles, setOpenConfirm) => [
  {
    key: 'setToPendingExclusion',
    text: (
      <MenuItem hide={!selectedTitles?.length}>
        <FlagOutlined className='icons-flag' /> Send to Pending Exclusion List
      </MenuItem>
    ),
    onSelect: () =>
      selectedTitles?.length && setOpenConfirm({ type: PENDING_EXCLUSION })
  }
];

export const toExclusion = (selectedTitles, setOpenConfirm) => [
  {
    key: 'setToExclusion',
    text: (
      <MenuItem hide={!selectedTitles?.length}>
        <FlagOutlined className='icons-flag' /> Send title(s) to Exclusion List
      </MenuItem>
    ),
    onSelect: () =>
      selectedTitles?.length && setOpenConfirm({ type: ALL_EXCLUDED })
  }
];

export const toPodiumTitles = (selectedTitles, setOpenConfirm) => [
  {
    key: 'setToPodiumTitle',
    text: (
      <MenuItem hide={!selectedTitles?.length}>
        <SolutionOutlined className='icons-podium-title' /> Send title(s) to Podium Titles
      </MenuItem>
    ),
    onSelect: () =>
      selectedTitles?.length && setOpenConfirm({ type: PODIUM_TITLES })
  }
];

export const toWatchlist = (selectedTitles, setOpenConfirm) => [
  {
    key: 'setToWatchlist',
    text: (
      <MenuItem hide={!selectedTitles?.length}>
        <EyeOutlined className='icons-eye' /> Send title(s) to Watchlist
      </MenuItem>
    ),
    onSelect: () =>
      selectedTitles?.length && setOpenConfirm({ type: WATCHLIST })
  }
];

export const toContacted = (selectedTitles, setOpenConfirm) => [
  {
    key: 'setToContacted',
    text: (
      <MenuItem hide={!selectedTitles?.length}>
        <PhoneOutlined className='actions-icons actions-icons-contact' /> Send title(s) to Contacted
      </MenuItem>
    ),
    onSelect: () =>
      selectedTitles?.length && setOpenConfirm({ type: CONTACTED })
  }
];

export const returnAll = (selectedTitles, setOpenConfirm) => [
  {
    key: 'returnAll',
    text: (
      <MenuItem hide={!selectedTitles?.length}>
        <RollbackOutlined className='icons-rollback' /> Return all title(s)
      </MenuItem>
    ),
    onSelect: () => selectedTitles?.length && setOpenConfirm({ type: RETURN })
  }
];

export const pinUnpinAll = (selectedTitles, selectedRows, setOpenPinUnpinModal) => {
  const allPinned = selectedRows?.length > 0 && selectedRows.every(row => Boolean(row.is_pinned_watchlist));
  const allUnpinned = selectedRows?.length > 0 && selectedRows.every(row => !row.is_pinned_watchlist);

  const options: any[] = [];

  if (!allPinned) {
    options.push({
      key: 'pinAll',
      text: (
        <MenuItem hide={!selectedTitles?.length}>
          <PushpinOutlined className='icons-pin' /> Pin title(s) to top
        </MenuItem>
      ),
      onSelect: () => selectedTitles?.length && setOpenPinUnpinModal?.('pin')
    });
  }

  if (!allUnpinned) {
    options.push({
      key: 'unpinAll',
      text: (
        <MenuItem hide={!selectedTitles?.length}>
          <PushpinFilled className='icons-unpin' /> Unpin title(s)
        </MenuItem>
      ),
      onSelect: () => selectedTitles?.length && setOpenPinUnpinModal?.('unpin')
    });
  }

  return options;
};

export const tabOptionsMapping = ({
  selectedTitlesKeys,
  selectedRows,
  setOpenConfirm,
  setOpenDeleteModal,
  setOpenBlockAuthorsModal,
  setOpenMuteAuthorsModal,
  setOpenUnmuteAuthorsModal,
  setOpenPinUnpinModal
}: {
  selectedTitlesKeys: [];
  selectedRows?: any[];
  setOpenConfirm: Dispatch<SetStateAction<{ type: string }>>;
  setOpenDeleteModal: Dispatch<SetStateAction<boolean>>;
  setOpenBlockAuthorsModal: Dispatch<SetStateAction<boolean>>;
  setOpenMuteAuthorsModal: Dispatch<SetStateAction<boolean>>;
  setOpenUnmuteAuthorsModal: Dispatch<SetStateAction<boolean>>;
  setOpenPinUnpinModal: Dispatch<SetStateAction<'pin' | 'unpin' | ''>>;
}) => ({
  [ALL_NEW_TITLE]: [
    ...toExclusion(selectedTitlesKeys, setOpenConfirm),
    ...toPodiumTitles(selectedTitlesKeys, setOpenConfirm),
    ...toWatchlist(selectedTitlesKeys, setOpenConfirm),
    ...toContacted(selectedTitlesKeys, setOpenConfirm),
    ...commonOptions(selectedTitlesKeys, {
      setOpenDeleteModal,
      setOpenBlockAuthorsModal,
      setOpenMuteAuthorsModal,
      showMute: true
    })
  ],
  [HOT_TITLES]: [
    ...toExclusion(selectedTitlesKeys, setOpenConfirm),
    ...toPodiumTitles(selectedTitlesKeys, setOpenConfirm),
    ...toWatchlist(selectedTitlesKeys, setOpenConfirm),
    ...toContacted(selectedTitlesKeys, setOpenConfirm),
    ...commonOptions(selectedTitlesKeys, {
      setOpenDeleteModal,
      setOpenBlockAuthorsModal,
      setOpenMuteAuthorsModal
    })
  ],
  [WATCHLIST]: [
    ...pinUnpinAll(selectedTitlesKeys, selectedRows, setOpenPinUnpinModal),
    ...toExclusion(selectedTitlesKeys, setOpenConfirm),
    ...toPodiumTitles(selectedTitlesKeys, setOpenConfirm),
    ...toContacted(selectedTitlesKeys, setOpenConfirm),
    ...returnAll(selectedTitlesKeys, setOpenConfirm),
    ...commonOptions(selectedTitlesKeys, {
      setOpenDeleteModal,
      setOpenBlockAuthorsModal,
      setOpenMuteAuthorsModal
    })
  ],
  [CONTACTED]: [
    ...toExclusion(selectedTitlesKeys, setOpenConfirm),
    ...toPodiumTitles(selectedTitlesKeys, setOpenConfirm),
    ...returnAll(selectedTitlesKeys, setOpenConfirm),
    ...commonOptions(selectedTitlesKeys, {
      setOpenDeleteModal,
      setOpenBlockAuthorsModal,
      setOpenMuteAuthorsModal
    })
  ],
  [PENDING_EXCLUSION]: [
    ...toExclusion(selectedTitlesKeys, setOpenConfirm),
    ...toWatchlist(selectedTitlesKeys, setOpenConfirm),
    ...toPodiumTitles(selectedTitlesKeys, setOpenConfirm),
    ...returnAll(selectedTitlesKeys, setOpenConfirm),
    ...commonOptions(selectedTitlesKeys, {
      setOpenDeleteModal,
      setOpenBlockAuthorsModal,
      setOpenMuteAuthorsModal
    })
  ],
  [MUTED_AUTHORS]: [
    ...toExclusion(selectedTitlesKeys, setOpenConfirm),
    ...toWatchlist(selectedTitlesKeys, setOpenConfirm),
    ...toPodiumTitles(selectedTitlesKeys, setOpenConfirm),
    ...commonOptions(selectedTitlesKeys, {
      setOpenDeleteModal,
      setOpenBlockAuthorsModal,
      setOpenUnmuteAuthorsModal,
      showUnmute: true
    })
  ],
  [ALL_EXCLUDED]: [
    ...toWatchlist(selectedTitlesKeys, setOpenConfirm),
    ...toPodiumTitles(selectedTitlesKeys, setOpenConfirm),
    ...returnAll(selectedTitlesKeys, setOpenConfirm),
    ...commonOptions(selectedTitlesKeys, {
      setOpenDeleteModal,
      setOpenBlockAuthorsModal,
      setOpenMuteAuthorsModal
    })
  ],
  [MANUALLY_ADDED]: [
    ...toExclusion(selectedTitlesKeys, setOpenConfirm),
    ...toWatchlist(selectedTitlesKeys, setOpenConfirm),
    ...toPodiumTitles(selectedTitlesKeys, setOpenConfirm),
    ...commonOptions(selectedTitlesKeys, {
      setOpenDeleteModal,
      setOpenBlockAuthorsModal,
      setOpenMuteAuthorsModal
    })
  ],
  [COMPETITORS]: [
    ...toWatchlist(selectedTitlesKeys, setOpenConfirm),
    ...toPodiumTitles(selectedTitlesKeys, setOpenConfirm),
    ...returnAll(selectedTitlesKeys, setOpenConfirm),
    ...commonOptions(selectedTitlesKeys, {
      setOpenDeleteModal,
      setOpenBlockAuthorsModal,
      setOpenMuteAuthorsModal
    })
  ],
  [PODIUM_TITLES]: [
    ...toWatchlist(selectedTitlesKeys, setOpenConfirm),
    ...returnAll(selectedTitlesKeys, setOpenConfirm),
    ...commonOptions(selectedTitlesKeys, {
      setOpenDeleteModal,
      setOpenBlockAuthorsModal,
      setOpenMuteAuthorsModal
    })
  ],
  [SEND_TO_CRM]: [
    ...toExclusion(selectedTitlesKeys, setOpenConfirm),
    ...toWatchlist(selectedTitlesKeys, setOpenConfirm),
    ...toPodiumTitles(selectedTitlesKeys, setOpenConfirm),
    ...returnAll(selectedTitlesKeys, setOpenConfirm),
    ...commonOptions(selectedTitlesKeys, {
      setOpenDeleteModal,
      setOpenBlockAuthorsModal,
      setOpenMuteAuthorsModal
    })
  ],
  [ALL_TITLES_RR]: [
    ...toExclusion(selectedTitlesKeys, setOpenConfirm),
    ...toWatchlist(selectedTitlesKeys, setOpenConfirm),
    ...toContacted(selectedTitlesKeys, setOpenConfirm),
    ...toPodiumTitles(selectedTitlesKeys, setOpenConfirm),
    ...commonOptions(selectedTitlesKeys, {
      setOpenDeleteModal,
      setOpenBlockAuthorsModal,
      setOpenMuteAuthorsModal,
      showMute: true
    })
  ],
  [WATCHLIST_RR]: [
    ...pinUnpinAll(selectedTitlesKeys, selectedRows, setOpenPinUnpinModal),
    ...toExclusion(selectedTitlesKeys, setOpenConfirm),
    ...toPodiumTitles(selectedTitlesKeys, setOpenConfirm),
    ...toContacted(selectedTitlesKeys, setOpenConfirm),
    ...returnAll(selectedTitlesKeys, setOpenConfirm),
    ...commonOptions(selectedTitlesKeys, {
      setOpenDeleteModal,
      setOpenBlockAuthorsModal,
      setOpenMuteAuthorsModal
    })
  ],
  [CONTACTED_RR]: [
    ...toExclusion(selectedTitlesKeys, setOpenConfirm),
    ...toPodiumTitles(selectedTitlesKeys, setOpenConfirm),
    ...returnAll(selectedTitlesKeys, setOpenConfirm),
    ...commonOptions(selectedTitlesKeys, {
      setOpenDeleteModal,
      setOpenBlockAuthorsModal,
      setOpenMuteAuthorsModal
    })
  ],
  [POPULAR]: [
    ...toExclusion(selectedTitlesKeys, setOpenConfirm),
    ...toWatchlist(selectedTitlesKeys, setOpenConfirm),
    ...commonOptions(selectedTitlesKeys, {
      setOpenDeleteModal,
      setOpenBlockAuthorsModal,
      setOpenMuteAuthorsModal
    })
  ],
  [TRENDING]: [
    ...toExclusion(selectedTitlesKeys, setOpenConfirm),
    ...toWatchlist(selectedTitlesKeys, setOpenConfirm),
    ...commonOptions(selectedTitlesKeys, {
      setOpenDeleteModal,
      setOpenBlockAuthorsModal,
      setOpenMuteAuthorsModal
    })
  ],
  [RISING_STARS]: [
    ...toExclusion(selectedTitlesKeys, setOpenConfirm),
    ...toWatchlist(selectedTitlesKeys, setOpenConfirm),
    ...commonOptions(selectedTitlesKeys, {
      setOpenDeleteModal,
      setOpenBlockAuthorsModal,
      setOpenMuteAuthorsModal
    })
  ],
  [PENDING_EXCLUSION_RR]: [
    ...toExclusion(selectedTitlesKeys, setOpenConfirm),
    ...toWatchlist(selectedTitlesKeys, setOpenConfirm),
    ...toPodiumTitles(selectedTitlesKeys, setOpenConfirm),
    ...returnAll(selectedTitlesKeys, setOpenConfirm),
    ...commonOptions(selectedTitlesKeys, {
      setOpenDeleteModal,
      setOpenBlockAuthorsModal,
      setOpenMuteAuthorsModal
    })
  ],
  [MUTED_AUTHORS_RR]: [
    ...toExclusion(selectedTitlesKeys, setOpenConfirm),
    ...toWatchlist(selectedTitlesKeys, setOpenConfirm),
    ...toPodiumTitles(selectedTitlesKeys, setOpenConfirm),
    ...commonOptions(selectedTitlesKeys, {
      setOpenDeleteModal,
      setOpenBlockAuthorsModal,
      setOpenUnmuteAuthorsModal,
      showUnmute: true
    })
  ],
  [ALL_EXCLUDED_RR]: [
    ...toWatchlist(selectedTitlesKeys, setOpenConfirm),
    ...toPodiumTitles(selectedTitlesKeys, setOpenConfirm),
    ...returnAll(selectedTitlesKeys, setOpenConfirm),
    ...commonOptions(selectedTitlesKeys, {
      setOpenDeleteModal,
      setOpenBlockAuthorsModal,
      setOpenMuteAuthorsModal
    })
  ],
  [PODIUM_TITLES_RR]: [
    ...toWatchlist(selectedTitlesKeys, setOpenConfirm),
    ...returnAll(selectedTitlesKeys, setOpenConfirm),
    ...commonOptions(selectedTitlesKeys, {
      setOpenDeleteModal,
      setOpenBlockAuthorsModal,
      setOpenMuteAuthorsModal
    })
  ],
  [SENT_TO_CRM_RR]: [
    ...toExclusion(selectedTitlesKeys, setOpenConfirm),
    ...toWatchlist(selectedTitlesKeys, setOpenConfirm),
    ...toPodiumTitles(selectedTitlesKeys, setOpenConfirm),
    ...returnAll(selectedTitlesKeys, setOpenConfirm),
    ...commonOptions(selectedTitlesKeys, {
      setOpenDeleteModal,
      setOpenBlockAuthorsModal,
      setOpenMuteAuthorsModal
    })
  ],
  [POPULAR_AO3]: [
    ...toExclusion(selectedTitlesKeys, setOpenConfirm),
    ...toWatchlist(selectedTitlesKeys, setOpenConfirm),
    ...toContacted(selectedTitlesKeys, setOpenConfirm),
    ...toPodiumTitles(selectedTitlesKeys, setOpenConfirm),
    ...commonOptions(selectedTitlesKeys, {
      setOpenDeleteModal,
      setOpenBlockAuthorsModal,
      setOpenMuteAuthorsModal,
      showMute: true
    })
  ],
  [WATCHLIST_AO3]: [
    ...pinUnpinAll(selectedTitlesKeys, selectedRows, setOpenPinUnpinModal),
    ...toExclusion(selectedTitlesKeys, setOpenConfirm),
    ...toPodiumTitles(selectedTitlesKeys, setOpenConfirm),
    ...toContacted(selectedTitlesKeys, setOpenConfirm),
    ...returnAll(selectedTitlesKeys, setOpenConfirm),
    ...commonOptions(selectedTitlesKeys, {
      setOpenDeleteModal,
      setOpenBlockAuthorsModal,
      setOpenMuteAuthorsModal
    })
  ],
  [CONTACTED_AO3]: [
    ...toExclusion(selectedTitlesKeys, setOpenConfirm),
    ...toPodiumTitles(selectedTitlesKeys, setOpenConfirm),
    ...returnAll(selectedTitlesKeys, setOpenConfirm),
    ...commonOptions(selectedTitlesKeys, {
      setOpenDeleteModal,
      setOpenBlockAuthorsModal,
      setOpenMuteAuthorsModal
    })
  ],
  [PODIUM_TITLES_AO3]: [
    ...toWatchlist(selectedTitlesKeys, setOpenConfirm),
    ...returnAll(selectedTitlesKeys, setOpenConfirm),
    ...commonOptions(selectedTitlesKeys, {
      setOpenDeleteModal,
      setOpenBlockAuthorsModal,
      setOpenMuteAuthorsModal
    })
  ],
  [PENDING_EXCLUSION_AO3]: [
    ...toExclusion(selectedTitlesKeys, setOpenConfirm),
    ...toWatchlist(selectedTitlesKeys, setOpenConfirm),
    ...toPodiumTitles(selectedTitlesKeys, setOpenConfirm),
    ...returnAll(selectedTitlesKeys, setOpenConfirm),
    ...commonOptions(selectedTitlesKeys, {
      setOpenDeleteModal,
      setOpenBlockAuthorsModal,
      setOpenMuteAuthorsModal
    })
  ],
  [ALL_EXCLUDED_AO3]: [
    ...toWatchlist(selectedTitlesKeys, setOpenConfirm),
    ...toPodiumTitles(selectedTitlesKeys, setOpenConfirm),
    ...returnAll(selectedTitlesKeys, setOpenConfirm),
    ...commonOptions(selectedTitlesKeys, {
      setOpenDeleteModal,
      setOpenBlockAuthorsModal,
      setOpenMuteAuthorsModal
    })
  ],
  [MUTED_AUTHORS_AO3]: [
    ...toExclusion(selectedTitlesKeys, setOpenConfirm),
    ...toWatchlist(selectedTitlesKeys, setOpenConfirm),
    ...toPodiumTitles(selectedTitlesKeys, setOpenConfirm),
    ...commonOptions(selectedTitlesKeys, {
      setOpenDeleteModal,
      setOpenBlockAuthorsModal,
      setOpenUnmuteAuthorsModal,
      showUnmute: true
    })
  ],
  [SENT_TO_CRM_AO3]: [
    ...toExclusion(selectedTitlesKeys, setOpenConfirm),
    ...toWatchlist(selectedTitlesKeys, setOpenConfirm),
    ...toPodiumTitles(selectedTitlesKeys, setOpenConfirm),
    ...returnAll(selectedTitlesKeys, setOpenConfirm),
    ...commonOptions(selectedTitlesKeys, {
      setOpenDeleteModal,
      setOpenBlockAuthorsModal,
      setOpenMuteAuthorsModal
    })
  ]
});